import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  IndexTable,
  InlineStack,
  Page,
  Text,
  TextField,
} from '@shopify/polaris';

import { api } from '@/api/client';

interface Discount {
  code: string;
  percent_off: number;
  starts_at: number | null;
  ends_at: number | null;
  max_redemptions: number | null;
  redeemed_count: number;
  note: string | null;
  disabled_at: number | null;
}

export function DiscountsPage() {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [code, setCode] = useState('');
  const [percent, setPercent] = useState('20');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    void api<{ discounts: Discount[] }>('/ops/api/discounts')
      .then((r) => setDiscounts(r.discounts ?? []))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const create = useCallback(async () => {
    setError(null);
    try {
      await api('/ops/api/discounts', {
        method: 'POST',
        body: JSON.stringify({
          code: code.trim(),
          percentOff: Number(percent),
          note: note.trim(),
        }),
      });
      setCode('');
      setNote('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [code, percent, note, load]);

  const toggle = useCallback(
    async (d: Discount) => {
      try {
        await api(`/ops/api/discounts/${encodeURIComponent(d.code)}`, {
          method: 'PUT',
          body: JSON.stringify({ disabled: !d.disabled_at }),
        });
        load();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [load]
  );

  return (
    <Page title="Promo codes">
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <p>{error}</p>
          </Banner>
        )}

        <Card>
          <BlockStack gap="300">
            <TextField label="Code" value={code} onChange={setCode} autoComplete="off" />
            {/* ⚠ Never <input type="number"> — a focused number input turns a
                stray scroll into a silent edit. See CLAUDE.md. */}
            <TextField
              label="Percent off"
              value={percent}
              onChange={setPercent}
              autoComplete="off"
              inputMode="numeric"
              role="spinbutton"
              suffix="%"
              helpText="1–100. A zero-percent code is one that silently does nothing."
            />
            <TextField label="Note" value={note} onChange={setNote} autoComplete="off" />
            <InlineStack>
              <Button variant="primary" disabled={!code.trim()} onClick={() => void create()}>
                Create
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <IndexTable
            resourceName={{ singular: 'code', plural: 'codes' }}
            itemCount={discounts.length}
            selectable={false}
            headings={[
              { title: 'Code' },
              { title: 'Off' },
              { title: 'Redeemed' },
              { title: 'Status' },
              { title: '' },
            ]}
          >
            {discounts.map((d, i) => (
              <IndexTable.Row id={d.code} key={d.code} position={i}>
                <IndexTable.Cell>
                  <Text as="span" fontWeight="semibold">
                    {d.code}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>{d.percent_off}%</IndexTable.Cell>
                <IndexTable.Cell>
                  {d.redeemed_count}
                  {d.max_redemptions ? ` / ${d.max_redemptions}` : ''}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {d.disabled_at ? (
                    <Badge tone="critical">Disabled</Badge>
                  ) : (
                    <Badge tone="success">Active</Badge>
                  )}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Button size="slim" onClick={() => void toggle(d)}>
                    {d.disabled_at ? 'Enable' : 'Disable'}
                  </Button>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        </Card>
      </BlockStack>
    </Page>
  );
}
